import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {find, get, map} from "lodash";
import moment from "moment";
import {Pencil} from "lucide-react";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {color, font, radius, type} from "common/tokens";
import Card from "./Card";
import CtaButton from "./CtaButton";
import SelectField from "./SelectField";

const useStyles = makeStyles({
  card: {
    padding: '28px 30px 30px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    background: color.ACCENT,
  },
  title: {
    ...type.cardHeading,
    color: color.TEXT,
    margin: 0,
  },
  sessionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '-8px 0 0',
  },
  body: {
    ...type.body,
    color: color.TEXT,
    margin: 0,
  },
  edit: {
    appearance: 'none',
    display: 'flex',
    padding: 4,
    border: 'none',
    background: 'none',
    color: color.TEXT,
    cursor: 'pointer',
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  option: {
    ...type.body,
    appearance: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    textAlign: 'left',
    background: color.SURFACE,
    border: `1.5px solid ${color.CARD_BORDER}`,
    borderRadius: 12,
    color: color.TEXT,
    cursor: 'pointer',
    '&:hover': {
      borderColor: color.ACCENT_TEXT,
    },
  },
  optionSelected: {
    borderColor: color.TERTIARY,
  },
  radio: {
    width: 20,
    height: 20,
    flexShrink: 0,
    borderRadius: radius.PILL,
    border: `1.5px solid ${color.BORDER}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    borderColor: color.TERTIARY,
    '&::after': {
      content: '""',
      width: 10,
      height: 10,
      borderRadius: radius.PILL,
      background: color.TERTIARY,
    },
  },
  when: {
    fontWeight: font.BOLD,
  },
  date: {
    color: color.TEXT_MUTED,
  },
  error: {
    ...type.meta,
    color: color.DANGER,
    margin: 0,
  },
  action: {
    alignSelf: 'flex-start',
    '&:disabled': {
      background: color.TERTIARY,
      color: color.ON_TERTIARY,
      opacity: 0.7,
    },
  },
});

const ScheduleCallCard = ({suggestions, sessionTitle, firstCall, onSchedule, sessionOptions, sessionKey, onChangeSession}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const schedule = async () => {
    setSubmitting(true);
    setFailed(false);
    try {
      await onSchedule(selected);
    } catch (e) {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant={'panel'} className={classes.card}>
      <h2 className={classes.title}>
        {firstCall ? t('scheduleFirstCall') : t('scheduleNextCall')}
      </h2>
      {Boolean(sessionTitle) && (
        <div className={classes.sessionRow}>
          <p className={classes.body}>
            {t('scheduleCallSession', {session: sessionTitle})}
          </p>
          {Boolean(onChangeSession) && (
            <SelectField
              options={sessionOptions}
              value={sessionKey}
              onChange={onChangeSession}
              renderTrigger={open => (
                <button
                  type={'button'}
                  className={classes.edit}
                  aria-label={t('changeSession')}
                  onClick={open}>
                  <Pencil size={16} strokeWidth={2.25}/>
                </button>
              )}/>
          )}
        </div>
      )}

      <div className={classes.options} role={'radiogroup'}>
        {map(suggestions, suggestion => {
          const when = moment(suggestion.scheduledTime);
          const isSelected = get(selected, 'scheduledTime') === suggestion.scheduledTime;
          return (
            <button
              type={'button'}
              key={suggestion.scheduledTime}
              role={'radio'}
              aria-checked={isSelected}
              className={classNames(classes.option, isSelected && classes.optionSelected)}
              onClick={() => setSelected(suggestion)}>
              <span className={classNames(classes.radio, isSelected && classes.radioSelected)}/>
              <span className={classes.when}>{when.format('dddd - h:mm A')}</span>
              <span className={classes.date}>({when.format('D MMMM')})</span>
            </button>
          );
        })}
      </div>

      {failed && <p className={classes.error} role={'alert'}>{t('scheduleCallFailed')}</p>}
      <CtaButton
        variant={'info'}
        className={classes.action}
        size={'large'}
        label={t('scheduleMentoringCall')}
        disabled={!find(suggestions, {scheduledTime: get(selected, 'scheduledTime')})}
        loading={submitting}
        onClick={schedule}/>
    </Card>
  );
};

export default ScheduleCallCard;
